import * as proto from 'src/proto/compiled-protos';
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActionComponent } from 'src/app/general/components/action/action.component';
import { BusinessHeaderComponent } from '../../components/business-header/business-header.component';
import { CampaignsComponent } from '../../components/campaigns/campaigns.component';
import { LocationHeaderComponent } from '../../components/location-header/location-header.component';
import { PageComponent } from '../../components/page/page.component';
import { ActivatedRoute } from '@angular/router';
import { RouteUtil } from 'src/app/general/util/route-util';
import { Util } from 'src/app/general/util/util';
import { NavigationService } from '../../services/navigation.service';
import { SessionService } from '../../services/session.service';
import { AuthenticationStatus, BusinessOrLocationId } from '../../util/util';
import { BackendService } from '../../services/backend.service';
import { LoadingController } from 'src/app/general/components/loadable-content/loading-controler';
import { ErrorResult, LoadingMessage } from 'src/app/general/util/result';
import { LoadableContentComponent } from 'src/app/general/components/loadable-content/loadable-content.component';
import { MenuEditorComponent } from '../../components/menu/menu-editor/menu-editor.component';
import { DeviceService } from 'src/app/general/services/device.service';
import { HeadingComponent } from 'src/app/general/components/heading/heading.component';
import { DialogService } from 'src/app/general/services/dialog.service';
import { ToastService } from 'src/app/general/services/toast.service';
import { AlertComponent } from 'src/app/general/components/alert/alert.component';

@Component({
  selector: 'app-edit-menu-page',
  standalone: true,
  imports: [
    CommonModule,
    PageComponent,
    CampaignsComponent,
    BusinessHeaderComponent,
    LocationHeaderComponent,
    ActionComponent,
    TranslateModule,
    LoadableContentComponent,
    MenuEditorComponent,
    HeadingComponent,
    AlertComponent
  ],
  templateUrl: './edit-menu-page.component.html',
  styleUrls: ['./edit-menu-page.component.css']
})
export class EditMenuPageComponent implements OnInit {

  businessId: string;
  locationId: string;

  menuSpec: proto.waiternow.common.MenuSpecProto;
  draftGeneratedFromParentBusiness: boolean;

  isLocationMenuEmpty: boolean;
  isBusinessMenuEmpty: boolean;

  menuLoadingController: LoadingController;

  constructor(
      private sessionService: SessionService,
      private activatedRoute: ActivatedRoute,
      private backendService: BackendService,
      private dialogService: DialogService,
      private translateService: TranslateService,
      private toastService: ToastService,
      public navigationService: NavigationService,
      public deviceService: DeviceService) {
    this.businessId = '';
    this.locationId = '';
    this.menuSpec = new proto.waiternow.common.MenuSpecProto();
    this.menuLoadingController = new LoadingController();
    this.draftGeneratedFromParentBusiness = false;
    this.isLocationMenuEmpty = false;
    this.isBusinessMenuEmpty = false;
  }

  ngOnInit(): void {
    if (this.sessionService.enforceAuthentication()
        == AuthenticationStatus.USER_NOT_AUTHENTICATED_AND_REDIRECTED_TO_SIGNIN_PAGE) {
      return;
    }

    const pathParams = RouteUtil.getPathParams(this.activatedRoute);
    this.businessId = Util.safeString(pathParams.get('businessId'));
    if (!this.businessId) {
      this.locationId = Util.safeString(pathParams.get('locationId'));
    }
    this.loadMenuSpec();
  }

  public loadMenuSpec(): void {
    this.menuLoadingController.onLoadingStarted(LoadingMessage.empty());
    this.backendService.getDraftOfMenu(
      new BusinessOrLocationId(this.businessId, this.locationId),
      /* onSuccess= */ response => {
        if (response?.menu) {
          this.menuSpec = new proto.waiternow.common.MenuSpecProto(response.menu);
          this.draftGeneratedFromParentBusiness = response.draftGeneratedFromParentBusiness;

          if (this.locationId) {
            this.isLocationMenuEmpty = this.isMenuSpecEmpty(this.menuSpec);
            if(this.isLocationMenuEmpty) {
              this.checkIfBusinessMenuIsEmptyFromLocationMenuEditorBestEffort();
            }
          }
        }
        this.menuLoadingController.onSuccess();
      },
      /* onError= */ error => {
        this.menuLoadingController.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_fetching_menu'));
      }
    );
  }

  public saveDraft(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.backendService.saveDraftOfMenu(
      new BusinessOrLocationId(this.businessId, this.locationId),
      this.menuSpec,
      /* onSuccess= */ () => {
        if (this.locationId) {
          this.isLocationMenuEmpty = this.isMenuSpecEmpty(this.menuSpec);
        } else {
          this.isBusinessMenuEmpty = this.isMenuSpecEmpty(this.menuSpec);
        }
        closeProgressDialog();
        this.translateService.get('saved').subscribe(text => this.toastService.success(text));
      },
      /* onError */ error => {
        closeProgressDialog();
        this.translateService.get('error_saving_menu_draft').subscribe(text => this.toastService.error(error.addErrorDetails(text)));
      }
    );
  }

  public deleteDraft(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.translateService.get('confirmation_delete_menu_draft').subscribe(text => {
      this.dialogService.openConfirmationDialog(
        text,
        /* onYes */ () => {
          this.backendService.removeDraftOfMenu(
            new BusinessOrLocationId(this.businessId, this.locationId),
            /* onSuccess= */ () => {
              closeProgressDialog();
              this.translateService.get('deleted').subscribe(text => this.toastService.success(text));
              this.loadMenuSpec();
            },
            /* onError */ error => {
              closeProgressDialog();
              this.translateService.get('error_deleting_menu_draft').subscribe(text => this.toastService.error(error.addErrorDetails(text)));
            }
          );
        },
        /* onNo= */ () => {
          closeProgressDialog();
        });
    });
  }

  public publishMenu(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.translateService.get('confirmation_publish_menu').subscribe(text => {
      this.dialogService.openConfirmationDialog(
        text,
        /* onYes */ () => {
          this.backendService.publishMenu(
            new BusinessOrLocationId(this.businessId, this.locationId),
            this.menuSpec,
            /* onSuccess= */ () => {
              closeProgressDialog();
              this.translateService.get('published').subscribe(text => this.toastService.success(text));
              this.loadMenuSpec();
            },
            /* onError */ error => {
              closeProgressDialog();
              this.translateService.get('error_publishing_menu').subscribe(text => this.toastService.error(error.addErrorDetails(text)));
            }
          );
        },
        /* onNo= */ () => {
          closeProgressDialog();
        });
    });
  }

  public deleteMenu(): void {
    const closeProgressDialog = this.dialogService.openProgressDialog();
    this.translateService.get('confirmation_delete_menu').subscribe(text => {
      this.dialogService.openConfirmationDialog(
        text,
        /* onYes */ () => {
          this.backendService.removeMenu(
            new BusinessOrLocationId(this.businessId, this.locationId),
            /* onSuccess= */ () => {
              closeProgressDialog();
              this.translateService.get('deleted').subscribe(text => this.toastService.success(text));
              this.loadMenuSpec();
            },
            /* onError */ error => {
              closeProgressDialog();
              this.translateService.get('error_deleting_menu').subscribe(text => this.toastService.error(error.addErrorDetails(text)));
            }
          );
        },
        /* onNo= */ () => {
          closeProgressDialog();
        });
    });
  }

  private isMenuSpecEmpty(menu: proto.waiternow.common.IMenuSpecProto): boolean {
    return (!menu.categories || menu.categories.length === 0)
        && (!menu.items || menu.items.length === 0)
        && (!menu.itemSelections || menu.itemSelections.length === 0)
        && (!menu.menuItems || menu.menuItems.length === 0)
        && (!menu.menuItemSelections || menu.menuItemSelections.length === 0)
        && (!menu.combos || menu.combos.length === 0)
        && (!menu.schedules || menu.schedules.length === 0);
  }

  private checkIfBusinessMenuIsEmptyFromLocationMenuEditorBestEffort(): void {
    // In this case this.businessId is empty, the input to this component was locationId.
    this.backendService.getLocation(
      this.locationId,
      /* onSuccess= */ location => {
        if (location) {
          this.backendService.getDraftOfMenu(
            BusinessOrLocationId.forBusiness(Util.safeString(location.businessId)),
            /* onSuccess= */ response => {
              if (response?.menu) {
                this.isBusinessMenuEmpty = this.isMenuSpecEmpty(response?.menu);
              }
            },
            /* onError= */ error => {
              this.toastService.error(error.addErrorDetails('error_fetching_menu'));
            }
          );
        }
      },
      /* onError= */ error => {}
    );
  }
}
